import React, { useState, useEffect, useRef, useCallback } from "react";
import { Upload, FileText, Trash2, Loader2, AlertTriangle, Scale, Send, GitCompare, Download, RefreshCw, CalendarClock, X, FolderOpen } from "lucide-react";
import { legalUpload, legalList, legalGet, legalAnalyze, legalDelete, legalAsk, legalMessages, legalCompare, legalDeadlines, legalFolders, legalMove } from "../lib/api";
import { toast } from "sonner";

const ACCEPT = "image/*,application/pdf,.pdf,.doc,.docx,.txt,.rtf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword";

const RISK_COLORS = {
  low: { bg: "#DCFCE7", border: "#15803D", text: "#15803D", label: "Low Risk" },
  medium: { bg: "#FEF3C7", border: "#D97706", text: "#D97706", label: "Medium Risk" },
  high: { bg: "#FEE2E2", border: "#DC2626", text: "#DC2626", label: "High Risk" },
};

function RiskMeter({ level }) {
  const order = ["low", "medium", "high"];
  const idx = order.indexOf(level);
  return (
    <div className="flex gap-1.5">
      {order.map((l, i) => {
        const c = RISK_COLORS[l];
        return <div key={l} className="h-3 flex-1 rounded-full border-2 border-[#1A1C19]" style={{ background: i <= idx ? c.border : "#fff" }} />;
      })}
    </div>
  );
}

function DeadlinePill({ daysLeft }) {
  let cls = "bg-emerald-600 text-white";
  let txt = `${daysLeft} days`;
  if (daysLeft < 0) { cls = "bg-red-600 text-white animate-pulse"; txt = `${Math.abs(daysLeft)} days overdue`; }
  else if (daysLeft === 0) { cls = "bg-red-600 text-white animate-pulse"; txt = "Due today"; }
  else if (daysLeft <= 30) { cls = "bg-amber-500 text-black"; txt = `${daysLeft} days`; }
  return <span className={`font-extrabold px-3 py-1 rounded-full border-2 border-[#1A1C19] text-sm ${cls}`} data-testid={`legal-deadline-pill-${daysLeft}`}>{txt}</span>;
}

function AnalysisView({ analysis }) {
  const risk = RISK_COLORS[analysis.overall_risk] || RISK_COLORS.medium;
  const summaryText = buildExport(analysis);
  return (
    <div className="space-y-4" data-testid="legal-risk-report">
      <div className="rounded-2xl border-3 p-4" style={{ background: risk.bg, borderColor: risk.border }}>
        <div className="flex items-center justify-between mb-2">
          <span className="font-extrabold text-lg" style={{ color: risk.text }}>{risk.label}</span>
          <span className="text-sm font-bold text-[#50564F]">{analysis.doc_type}</span>
        </div>
        <RiskMeter level={analysis.overall_risk} />
        <p className="mt-3 text-lg leading-relaxed">{analysis.summary}</p>
      </div>

      {(analysis.risks || []).length > 0 && (
        <div className="space-y-2">
          <h4 className="yb-heading font-extrabold text-lg flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Risks & Remedies</h4>
          {analysis.risks.map((r, i) => {
            const rc = RISK_COLORS[r.severity] || RISK_COLORS.medium;
            return (
              <div key={i} className="bg-white rounded-xl border-2 border-[#1A1C19] p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-extrabold px-2 py-0.5 rounded-full text-white" style={{ background: rc.border }}>{r.severity}</span>
                  <span className="font-bold">{r.issue}</span>
                </div>
                {r.clause && <p className="text-sm text-[#50564F] italic">Clause: {r.clause}</p>}
                <p className="text-sm mt-1"><strong>Remedy:</strong> {r.remedy}</p>
              </div>
            );
          })}
        </div>
      )}

      {(analysis.key_terms || []).length > 0 && (
        <div className="bg-white rounded-xl border-2 border-[#1A1C19] p-3">
          <h4 className="font-extrabold mb-1">Key Terms</h4>
          <ul className="list-disc pl-5 space-y-0.5">{analysis.key_terms.map((t, i) => <li key={i}>{t}</li>)}</ul>
        </div>
      )}

      {(analysis.missing_or_recommended || []).length > 0 && (
        <div className="bg-[#FEF3C7] rounded-xl border-2 border-[#D97706] p-3">
          <h4 className="font-extrabold mb-1">Missing / Recommended</h4>
          <ul className="list-disc pl-5 space-y-0.5">{analysis.missing_or_recommended.map((t, i) => <li key={i}>{t}</li>)}</ul>
        </div>
      )}

      {(analysis.deadlines || []).length > 0 && (
        <div className="bg-white rounded-xl border-2 border-[#1A1C19] p-3">
          <h4 className="font-extrabold mb-1">Deadlines</h4>
          {analysis.deadlines.map((d, i) => (
            <div key={i} className="flex justify-between py-1 border-b border-[#e5e5e5] last:border-0">
              <span>{d.label} <span className="text-[#50564F]">({d.type})</span></span>
              <span className="font-bold">{d.date || "—"}</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-[#50564F] italic border-l-4 border-[#1A1C19] pl-3">{analysis.disclaimer}</p>

      <button
        data-testid="legal-export-button"
        onClick={() => { navigator.clipboard.writeText(summaryText); toast.success("Summary copied to clipboard"); }}
        className="yb-tactile w-full py-3 bg-[#2563EB] text-white font-bold flex items-center justify-center gap-2"
      >
        <Download className="w-5 h-5" /> Copy / Share Summary
      </button>
    </div>
  );
}

function buildExport(a) {
  const lines = [];
  lines.push(`CONTRACT ANALYSIS — ${a.doc_type || ""}`);
  lines.push(`Overall Risk: ${(a.overall_risk || "").toUpperCase()}`);
  lines.push("");
  lines.push("SUMMARY:\n" + (a.summary || ""));
  if ((a.risks || []).length) {
    lines.push("\nPROBLEMS & REMEDIES:");
    a.risks.forEach((r) => lines.push(`- [${r.severity}] ${r.issue} -> ${r.remedy}${r.clause ? ` (Clause: ${r.clause})` : ""}`));
  }
  if ((a.key_terms || []).length) lines.push("\nKEY TERMS:\n" + a.key_terms.map((t) => `- ${t}`).join("\n"));
  if ((a.missing_or_recommended || []).length) lines.push("\nMISSING / RECOMMENDED:\n" + a.missing_or_recommended.map((t) => `- ${t}`).join("\n"));
  if ((a.deadlines || []).length) lines.push("\nDEADLINES:\n" + a.deadlines.map((d) => `- ${d.label} (${d.type}): ${d.date || "—"}`).join("\n"));
  lines.push("\n" + (a.disclaimer || ""));
  return lines.join("\n");
}

export default function Legal({ session }) {
  const [docs, setDocs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [caseName, setCaseName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [active, setActive] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [tab, setTab] = useState("archive");
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [asking, setAsking] = useState(false);
  const [ctxDoc, setCtxDoc] = useState("");
  const [ctxFolder, setCtxFolder] = useState("");
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");
  const [cmpResult, setCmpResult] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [askState, setAskState] = useState("Auto");
  const uploadRef = useRef(null);

  const refresh = useCallback(async () => {
    const [d, dl, m, fl] = await Promise.all([
      legalList(session.user_id), legalDeadlines(session.user_id), legalMessages(session.user_id), legalFolders(session.user_id),
    ]);
    setDocs(d); setDeadlines(dl); setMessages(m); setFolders(fl);
  }, [session.user_id]);

  useEffect(() => { refresh(); }, []); // eslint-disable-line

  const doUpload = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      await legalUpload(session.user_id, Array.from(files), caseName.trim());
      toast.success(caseName.trim() ? `Filed into “${caseName.trim()}”` : "Contract uploaded");
      refresh();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const moveDoc = async (id, folder) => {
    try { await legalMove(id, folder); toast.success(folder ? `Moved to “${folder}”` : "Unfiled"); refresh(); }
    catch (e) { toast.error("Could not move"); }
  };

  const openDoc = async (doc) => {
    setActive(doc);
    setAnalysis(doc.analysis || null);
  };

  const runAnalyze = async () => {
    if (!active) return;
    setAnalyzing(true);
    try {
      const res = await legalAnalyze(active.id);
      if (res.status === "done" && res.analysis) {
        setAnalysis(res.analysis); refresh(); setAnalyzing(false); return;
      }
      // analysis runs in the background for large contracts — poll until ready
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const d = await legalGet(active.id);
          if (d.analysis) {
            clearInterval(poll); setActive(d); setAnalysis(d.analysis); refresh(); setAnalyzing(false);
          } else if (d.analysis_status === "error") {
            clearInterval(poll); toast.error(d.analysis_error || "Analysis failed"); setAnalyzing(false);
          } else if (tries > 60) {
            clearInterval(poll); toast.error("Analysis is taking too long — please try again."); setAnalyzing(false);
          }
        } catch (e) { /* keep polling */ }
      }, 3000);
    } catch (e) {
      toast.error("Analysis failed");
      setAnalyzing(false);
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    await legalDelete(id);
    if (active?.id === id) { setActive(null); setAnalysis(null); }
    refresh();
  };

  const doAsk = async () => {
    if (!question.trim()) return;
    setAsking(true);
    const q = question; setQuestion("");
    try {
      await legalAsk(session.user_id, q, ctxDoc || null, askState, ctxFolder || null);
      const m = await legalMessages(session.user_id);
      setMessages(m);
    } catch (e) {
      toast.error("Could not get an answer");
    } finally {
      setAsking(false);
    }
  };

  const doCompare = async () => {
    if (!cmpA || !cmpB || cmpA === cmpB) { toast.error("Pick two different contracts"); return; }
    setComparing(true);
    try {
      const res = await legalCompare(session.user_id, cmpA, cmpB);
      setCmpResult(res);
    } catch (e) {
      toast.error("Compare failed");
    } finally {
      setComparing(false);
    }
  };

  const tabs = [
    { id: "archive", label: "Contracts", icon: FileText },
    { id: "deadlines", label: "Deadlines", icon: CalendarClock },
    { id: "ask", label: "Ask", icon: Scale },
    { id: "compare", label: "Compare", icon: GitCompare },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#1A1C19] text-[#FFD600] flex items-center justify-center border-3 border-[#1A1C19]">
          <Scale className="w-7 h-7" />
        </div>
        <div>
          <h2 className="yb-heading text-2xl font-extrabold">Illinois Legal Consultant</h2>
          <p className="text-sm text-[#50564F]">AI guidance — not a substitute for an attorney</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`legal-tab-${t.id}`}
            className={`yb-tactile py-3 font-bold flex flex-col sm:flex-row items-center justify-center gap-1 text-sm ${tab === t.id ? "bg-[#1A1C19] text-white" : "bg-white"}`}>
            <t.icon className="w-5 h-5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "archive" && (
        <div className="space-y-4">
          <input value={caseName} onChange={(e) => setCaseName(e.target.value)} data-testid="legal-case-name"
            placeholder="Case / folder name (optional) — e.g. 123 Oak St Purchase" className="yb-tactile w-full px-4 py-3 bg-white font-bold outline-none" />
          <div
            data-testid="legal-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); doUpload(e.dataTransfer.files); }}
            onClick={() => !uploading && uploadRef.current?.click()}
            className={`yb-tactile w-full py-8 font-black text-xl flex flex-col items-center gap-2 cursor-pointer transition-colors ${dragOver ? "bg-[#15803D] text-white" : "bg-[#FFD600]"}`}
            style={{ boxShadow: "0 8px 0 0 #1A1C19" }}>
            {uploading ? <Loader2 className="w-10 h-10 animate-spin" /> : <Upload className="w-10 h-10" />}
            {dragOver ? "Drop files to file them" : "Drag & drop files here, or tap"}
            <span className="text-xs font-bold opacity-70">PDF · Word (.doc/.docx) · Images · Text</span>
          </div>
          <input ref={uploadRef} type="file" accept={ACCEPT} multiple className="hidden"
            data-testid="legal-file-input" onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }} />

          <div className="flex items-center justify-between">
            <h3 className="yb-heading font-extrabold text-lg">Case Folders ({docs.length} files)</h3>
            <button onClick={refresh} className="yb-tactile bg-white p-2"><RefreshCw className="w-5 h-5" /></button>
          </div>

          {active ? (
            <div className="bg-[#FAFAF7] rounded-2xl border-3 border-[#1A1C19] p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="yb-heading font-extrabold text-lg truncate">{active.title}</h3>
                <button onClick={() => { setActive(null); setAnalysis(null); }} className="yb-tactile bg-white p-2"><X className="w-5 h-5" /></button>
              </div>
              {analysis ? <AnalysisView analysis={analysis} /> : (
                <button onClick={runAnalyze} disabled={analyzing} data-testid="legal-analyze-button" className="yb-tactile w-full py-5 bg-[#FFD600] font-extrabold flex items-center justify-center gap-2">
                  {analyzing ? <Loader2 className="w-6 h-6 animate-spin" /> : <Scale className="w-6 h-6" />} Analyze Contract
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {docs.length === 0 && <p className="text-[#50564F]">No contracts yet. Name a case above, then drop your paperwork in.</p>}
              {folders.map((f) => (
                <div key={f.name} className="bg-[#FAFAF7] rounded-2xl border-2 border-[#1A1C19] p-3" data-testid={`legal-folder-${f.name}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <FolderOpen className="w-5 h-5 text-[#D97706]" />
                    <span className="yb-heading font-extrabold">{f.name}</span>
                    <span className="text-xs font-bold bg-white border border-[#1A1C19] rounded-full px-2 py-0.5">{f.count}</span>
                    {f.name !== "Unfiled" && (
                      <button onClick={() => { setTab("ask"); setCtxFolder(f.name); setCtxDoc(""); }}
                        data-testid={`legal-folder-ask-${f.name}`}
                        className="ml-auto text-xs font-bold text-[#2563EB] flex items-center gap-1"><Scale className="w-4 h-4" /> Cross-reference</button>
                    )}
                  </div>
                  <div className="space-y-2">
                    {f.docs.map((d) => (
                      <div key={d.id} onClick={() => openDoc(d)} data-testid={`legal-doc-${d.id}`} className="yb-tactile w-full bg-white p-3 flex items-center gap-3 text-left cursor-pointer">
                        <FileText className="w-5 h-5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-bold truncate">{d.title}</p>
                          <p className="text-sm text-[#50564F]">{d.analysis ? `Risk: ${d.analysis.overall_risk}` : "Not analyzed"}</p>
                        </div>
                        <select value={d.folder || ""} onClick={(e) => e.stopPropagation()} onChange={(e) => moveDoc(d.id, e.target.value)}
                          data-testid={`legal-move-${d.id}`} className="text-xs bg-[#FAFAF7] border border-[#1A1C19] rounded-lg px-1 py-1 max-w-[110px]">
                          <option value="">Unfiled</option>
                          {folders.filter((x) => x.name !== "Unfiled").map((x) => <option key={x.name} value={x.name}>{x.name}</option>)}
                          {caseName.trim() && !folders.some((x) => x.name === caseName.trim()) && <option value={caseName.trim()}>+ {caseName.trim()}</option>}
                        </select>
                        <span onClick={(e) => del(d.id, e)} data-testid={`legal-delete-${d.id}`} className="yb-tactile bg-[#DC2626] text-white p-2"><Trash2 className="w-4 h-4" /></span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "deadlines" && (
        <div className="space-y-2" data-testid="legal-deadlines-section">
          <h3 className="yb-heading font-extrabold text-lg">Upcoming Deadlines</h3>
          {deadlines.length === 0 && <p className="text-[#50564F]">No dated deadlines found. Analyze contracts to populate this.</p>}
          {deadlines.map((d, i) => (
            <div key={i} className="yb-tactile bg-white p-4 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-bold truncate">{d.label}</p>
                <p className="text-sm text-[#50564F] truncate">{d.doc_title} · {d.date} · {d.type}</p>
              </div>
              <DeadlinePill daysLeft={d.days_left} />
            </div>
          ))}
        </div>
      )}

      {tab === "ask" && (
        <div className="space-y-3">
          <select value={ctxFolder} onChange={(e) => { setCtxFolder(e.target.value); if (e.target.value) setCtxDoc(""); }} className="yb-tactile bg-white px-4 py-3 w-full font-bold" data-testid="legal-context-folder">
            <option value="">No case folder</option>
            {folders.filter((f) => f.name !== "Unfiled").map((f) => <option key={f.name} value={f.name}>📁 {f.name} ({f.count} files — cross-reference)</option>)}
          </select>
          <select value={ctxDoc} onChange={(e) => { setCtxDoc(e.target.value); if (e.target.value) setCtxFolder(""); }} className="yb-tactile bg-white px-4 py-3 w-full font-bold" data-testid="legal-context-select">
            <option value="">No single document context</option>
            {docs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
          <select value={askState} onChange={(e) => setAskState(e.target.value)} className="yb-tactile bg-white px-4 py-3 w-full font-bold" data-testid="legal-state-select">
            <option value="Auto">Auto-detect jurisdiction</option>
            {["Illinois", "Wisconsin", "Indiana", "Iowa"].map((s) => <option key={s} value={s}>{s} law</option>)}
          </select>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {messages.map((m, i) => (
              <div key={i} className={`p-3 rounded-2xl border-2 border-[#1A1C19] ${m.role === "user" ? "bg-[#FEFCE8] ml-8" : "bg-white mr-8"}`}>
                <p className="whitespace-pre-wrap">{m.text}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={question} onChange={(e) => setQuestion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doAsk()}
              placeholder="Ask: is there legal ground, our vulnerability, and the remedy?" data-testid="legal-ask-input"
              className="flex-1 yb-tactile bg-white px-4 py-3 outline-none" />
            <button onClick={doAsk} disabled={asking} data-testid="legal-ask-send" className="yb-tactile bg-[#15803D] text-white p-3">
              {asking ? <Loader2 className="w-6 h-6 animate-spin" /> : <Send className="w-6 h-6" />}
            </button>
          </div>
        </div>
      )}

      {tab === "compare" && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select value={cmpA} onChange={(e) => setCmpA(e.target.value)} className="yb-tactile bg-white px-4 py-3 font-bold" data-testid="legal-compare-a">
              <option value="">Contract A</option>
              {docs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
            <select value={cmpB} onChange={(e) => setCmpB(e.target.value)} className="yb-tactile bg-white px-4 py-3 font-bold" data-testid="legal-compare-b">
              <option value="">Contract B</option>
              {docs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </div>
          <button onClick={doCompare} disabled={comparing} data-testid="legal-compare-button" className="yb-tactile w-full py-4 bg-[#FFD600] font-extrabold flex items-center justify-center gap-2">
            {comparing ? <Loader2 className="w-6 h-6 animate-spin" /> : <GitCompare className="w-6 h-6" />} Compare
          </button>
          {cmpResult && (
            <div className="space-y-3" data-testid="legal-compare-result">
              {(cmpResult.differences || []).map((d, i) => (
                <div key={i} className="bg-white rounded-xl border-2 border-[#1A1C19] p-3">
                  <p className="font-extrabold">{d.aspect}</p>
                  <p className="text-sm"><strong>A:</strong> {d.contract_a}</p>
                  <p className="text-sm"><strong>B:</strong> {d.contract_b}</p>
                </div>
              ))}
              <div className="bg-[#DCFCE7] rounded-xl border-2 border-[#15803D] p-3">
                <p className="font-extrabold mb-1">Recommendation</p>
                <p>{cmpResult.recommendation}</p>
              </div>
              <p className="text-xs text-[#50564F] italic border-l-4 border-[#1A1C19] pl-3">{cmpResult.disclaimer}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
